let ringtoneContext: AudioContext | null = null;
let ringtoneIntervalId: number | null = null;
let pendingRingtone = false;
let outgoingDialToneTimeoutId: number | null = null;
let pendingOutgoingDialTone = false;
let unlockListenersInstalled = false;
let unlockHandler: (() => void) | null = null;

const OUTGOING_DIAL_TONE_REPEAT_MS = 4000;
const OUTGOING_DIAL_TONE_RETRY_MS = 500;

function markCallSoundsUnlockedForPage() {
  try {
    window.dispatchEvent(new Event('videocall:soundsUnlocked'));
  } catch {
    // ignore
  }
}

function beepOnce(context: AudioContext) {
  const oscillator = context.createOscillator();
  const gainNode = context.createGain();

  oscillator.connect(gainNode);
  gainNode.connect(context.destination);

  oscillator.frequency.value = 440;
  gainNode.gain.setValueAtTime(0.1, context.currentTime);

  oscillator.start(context.currentTime);
  oscillator.stop(context.currentTime + 0.2);
  oscillator.onended = () => {
    try {
      oscillator.disconnect();
      gainNode.disconnect();
    } catch {
      // ignore
    }
  };
}

function dialToneOnce(context: AudioContext) {
  const firstOscillator = context.createOscillator();
  const secondOscillator = context.createOscillator();
  const gainNode = context.createGain();
  const now = context.currentTime;

  firstOscillator.connect(gainNode);
  secondOscillator.connect(gainNode);
  gainNode.connect(context.destination);

  firstOscillator.frequency.value = 440;
  secondOscillator.frequency.value = 480;
  gainNode.gain.setValueAtTime(0.0001, now);
  gainNode.gain.exponentialRampToValueAtTime(0.08, now + 0.04);
  gainNode.gain.setValueAtTime(0.08, now + 1.8);
  gainNode.gain.exponentialRampToValueAtTime(0.0001, now + 2.0);

  firstOscillator.start(now);
  secondOscillator.start(now);
  firstOscillator.stop(now + 2.05);
  secondOscillator.stop(now + 2.05);
  firstOscillator.onended = () => {
    try {
      firstOscillator.disconnect();
      secondOscillator.disconnect();
      gainNode.disconnect();
    } catch {
      // ignore
    }
  };
}

function startRingtoneInternal() {
  if (!ringtoneContext) return;
  if (ringtoneContext.state !== 'running') return;
  if (ringtoneIntervalId !== null) return;

  beepOnce(ringtoneContext);
  ringtoneIntervalId = window.setInterval(() => {
    if (!ringtoneContext || ringtoneContext.state !== 'running') return;
    beepOnce(ringtoneContext);
  }, 1000);
}

function startOutgoingDialToneInternal() {
  if (!ringtoneContext) return;
  if (outgoingDialToneTimeoutId !== null) return;

  if (ringtoneContext.state !== 'running') {
    void ringtoneContext.resume().catch(() => {});
    outgoingDialToneTimeoutId = window.setTimeout(() => {
      outgoingDialToneTimeoutId = null;
      if (pendingOutgoingDialTone) startOutgoingDialToneInternal();
    }, OUTGOING_DIAL_TONE_RETRY_MS);
    return;
  }

  dialToneOnce(ringtoneContext);
  outgoingDialToneTimeoutId = window.setTimeout(() => {
    outgoingDialToneTimeoutId = null;
    if (pendingOutgoingDialTone) startOutgoingDialToneInternal();
  }, OUTGOING_DIAL_TONE_REPEAT_MS);
}

export async function tryUnlockRingtoneAudio(): Promise<boolean> {
  try {
    if (!ringtoneContext) ringtoneContext = new AudioContext();

    if (ringtoneContext.state !== 'running') {
      await ringtoneContext.resume();
    }

    if (!ringtoneContext || ringtoneContext.state !== 'running') return false;

    markCallSoundsUnlockedForPage();
    if (pendingRingtone) startRingtoneInternal();
    if (pendingOutgoingDialTone) startOutgoingDialToneInternal();

    if (unlockHandler) {
      window.removeEventListener('pointerdown', unlockHandler);
      window.removeEventListener('keydown', unlockHandler);
      window.removeEventListener('touchstart', unlockHandler);
      unlockHandler = null;
      unlockListenersInstalled = false;
    }

    return true;
  } catch {
    return false;
  }
}

export async function tryUnlockCallSounds(): Promise<boolean> {
  const webAudioAttempt = tryUnlockRingtoneAudio().catch(() => false);
  const webAudioOk = await webAudioAttempt;
  if (webAudioOk) {
    markCallSoundsUnlockedForPage();
    return true;
  }

  return false;
}

function installAudioUnlockListeners() {
  if (unlockListenersInstalled) return;
  unlockListenersInstalled = true;

  unlockHandler = () => {
    void tryUnlockRingtoneAudio();
  };

  window.addEventListener('pointerdown', unlockHandler, { passive: true });
  window.addEventListener('keydown', unlockHandler, { passive: true });
  window.addEventListener('touchstart', unlockHandler, { passive: true });
}

export function playRingtone() {
  pendingRingtone = true;
  stopOutgoingDialTone();
  installAudioUnlockListeners();

  if (!ringtoneContext) return;
  startRingtoneInternal();
}

export function stopRingtone() {
  pendingRingtone = false;

  if (ringtoneIntervalId !== null) {
    clearInterval(ringtoneIntervalId);
    ringtoneIntervalId = null;
  }

  if (unlockHandler) {
    window.removeEventListener('pointerdown', unlockHandler);
    window.removeEventListener('keydown', unlockHandler);
    window.removeEventListener('touchstart', unlockHandler);
    unlockHandler = null;
    unlockListenersInstalled = false;
  }
}

export function playOutgoingDialTone() {
  pendingOutgoingDialTone = true;
  stopRingtone();
  installAudioUnlockListeners();

  if (!ringtoneContext) {
    void tryUnlockRingtoneAudio();
    return;
  }
  startOutgoingDialToneInternal();
}

export function stopOutgoingDialTone() {
  pendingOutgoingDialTone = false;

  if (outgoingDialToneTimeoutId !== null) {
    clearTimeout(outgoingDialToneTimeoutId);
    outgoingDialToneTimeoutId = null;
  }
}
